/**
 * Variant Collection Filters
 * Handles category filtering for collection pages with variant-expanded display
 */

class VariantCollectionFilters {
  constructor() {
    this.filterButtons = document.querySelectorAll('.filter-btn');
    this.allButton = Array.from(this.filterButtons).find(btn => btn.dataset.filter === 'All');
    this.productCards = document.querySelectorAll('.product-card');
    this.ctaElements = document.querySelectorAll('[data-cta="true"]');
    this.productGrid = document.getElementById('productGrid');
    this.noResultsMessage = document.getElementById('noResultsMessage');
    this.activeFilters = new Set();

    this.init();
  }

  init() {
    if (!this.filterButtons.length) return;

    // Add click handlers to filter buttons
    this.filterButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleFilterClick(e));
    });

    // Load filters from URL on page load
    this.loadFiltersFromURL();
  }

  handleFilterClick(event) {
    const button = event.currentTarget;
    const filterValue = button.dataset.filter;

    // Handle "All" button click
    if (filterValue === 'All') {
      this.clearAllFilters();
      return;
    }

    // Toggle filter
    if (this.activeFilters.has(filterValue)) {
      this.activeFilters.delete(filterValue);
      button.classList.remove('active');
    } else {
      this.activeFilters.add(filterValue);
      button.classList.add('active');
    }

    // Update All button state
    this.updateAllButton();

    // Apply filters
    this.applyFilters();

    // Update URL
    this.updateURL();
  }

  applyFilters() {
    let visibleCount = 0;

    this.productCards.forEach(card => {
      const cardCategory = card.dataset.category;

      // If no filters are active, show all cards
      if (this.activeFilters.size === 0) {
        card.style.display = '';
        visibleCount++;
      } else {
        // Show card if it matches any active filter
        if (this.activeFilters.has(cardCategory)) {
          card.style.display = '';
          visibleCount++;
        } else {
          card.style.display = 'none';
        }
      }
    });

    // Hide CTAs when filters are active, show when no filters
    this.ctaElements.forEach(cta => {
      if (this.activeFilters.size === 0) {
        cta.style.display = '';
      } else {
        cta.style.display = 'none';
      }
    });

    // Show/hide no results message
    this.toggleNoResults(visibleCount);
  }

  toggleNoResults(visibleCount) {
    if (visibleCount === 0 && this.activeFilters.size > 0) {
      if (this.noResultsMessage) {
        this.noResultsMessage.style.display = 'block';
      }
      if (this.productGrid) {
        this.productGrid.style.display = 'none';
      }
    } else {
      if (this.noResultsMessage) {
        this.noResultsMessage.style.display = 'none';
      }
      if (this.productGrid) {
        this.productGrid.style.display = '';
      }
    }
  }

  clearAllFilters() {
    // Remove active class from all filter buttons except All
    this.filterButtons.forEach(button => {
      if (button.dataset.filter === 'All') {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });

    // Clear active filters
    this.activeFilters.clear();

    // Apply filters (show all)
    this.applyFilters();

    // Update URL
    this.updateURL();
  }

  updateAllButton() {
    if (!this.allButton) return;

    // All button should be active only when no other filters are selected
    if (this.activeFilters.size === 0) {
      this.allButton.classList.add('active');
    } else {
      this.allButton.classList.remove('active');
    }
  }

  updateURL() {
    const url = new URL(window.location);

    if (this.activeFilters.size > 0) {
      const filterString = Array.from(this.activeFilters).join(',');
      url.searchParams.set('categories', filterString);
    } else {
      url.searchParams.delete('categories');
    }

    // Update URL without page reload
    window.history.replaceState({}, '', url);
  }

  loadFiltersFromURL() {
    const url = new URL(window.location);
    const categoriesParam = url.searchParams.get('categories');

    if (categoriesParam) {
      const categories = categoriesParam.split(',');

      categories.forEach(category => {
        this.activeFilters.add(category);

        // Mark button as active
        const button = Array.from(this.filterButtons).find(
          btn => btn.dataset.filter === category
        );
        if (button) {
          button.classList.add('active');
        }
      });

      // Update All button state
      this.updateAllButton();

      // Apply filters
      this.applyFilters();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantCollectionFilters();
  });
} else {
  new VariantCollectionFilters();
}
